import * as React from 'react';
import { getResident, type GetResidentResponse } from '@/api/residents';
import { Box, Button, Container, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react/dist/ssr/UsersThree';

import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

import { UserHeader } from './components/user-header';
import { ResidentDetails } from './components/user-details';
import { CommunityRolesSection } from './components/community-and-unit-details';
import { UnitDetailsSection } from './components/community-and-unit-details';
import { ResidentCustomFields } from './components/resident-custom-fields';
import { UserSidebar } from './components/user-sidebar';
import { ArchiveUserCard } from './components/archive-user-card';
import Grid from '@mui/system/Unstable_Grid';

export function ResidentPage(): React.JSX.Element {
  const { community } = useUser();
  const { userId } = useParams<{ userId: string }>();
  const theme = useTheme();

  const { data, isLoading, error } = useQuery<GetResidentResponse | null>({
    queryKey: ['residents', community?.id, userId ?? ''],
    queryFn: async () => {
      const res = await getResident(community?.id, userId ?? '', {
        include: 'units,units.unitType,communityRoles',
      });
      return res;
    },
    retry: false,
  });

  const resident = data?.resident ?? null;
  const customFields = data?.custom_fields ?? [];
  const customFieldValues = data?.custom_field_values ?? {};
  const { user: currentUser } = useUser();
  const isManager = true;
  const isViewingSelf = currentUser?.id === resident?.id;

  if (resident?.units && resident.units.length > 0) {
    resident.units.sort((a, b) => {
      const moveOutDateA = a.pivot.move_out_date ? new Date(a.pivot.move_out_date).getTime() : 0;
      const moveOutDateB = b.pivot.move_out_date ? new Date(b.pivot.move_out_date).getTime() : 0;
      return moveOutDateA - moveOutDateB;
    });
  }

  if (!isLoading && error?.message === 'User not found') {
    return (
      <>
        <Seo title="User Not Found" />
        <Box component="main" sx={{ flexGrow: 1, py: 3, backgroundColor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center' }}>
              <UsersThreeIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                User not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This user doesn't exist or you don't have access to them.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">Go Back</Button>
                <Button href={paths.users.index} variant="contained">All Users</Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title={resident ? `${resident.first_name ?? ''} ${resident.last_name ?? ''}`.trim() || resident.email : 'User'} />
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
        {/* Header strip */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <UserHeader resident={resident} isLoading={isLoading} />
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            {/* ── Left column: editable content ── */}
            <Grid md={8} xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Contact details */}
                <Box sx={{ p: 3 }}>
                  <ResidentDetails isLoading={isLoading} resident={resident} />
                </Box>

                {/* Community roles & title */}
                <Divider />
                <Box sx={{ p: 3 }}>
                  <CommunityRolesSection
                    communityRoles={resident?.community_roles || []}
                    communityTitle={resident?.community_roles?.[0]?.pivot?.user_title_id || null}
                    loading={isLoading}
                  />
                </Box>

                {/* Unit details */}
                <Divider />
                <Box sx={{ p: 3 }}>
                  <UnitDetailsSection
                    units={resident?.units || []}
                    loading={isLoading}
                  />
                </Box>

                {/* Custom fields (conditional) */}
                {customFields.length > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                      <ResidentCustomFields
                        communityId={community!.id}
                        residentId={resident?.uuid ?? resident?.id ?? ''}
                        customFields={customFields}
                        customFieldValues={customFieldValues}
                        isManager={isManager}
                        isViewingSelf={isViewingSelf}
                        queryKey={['residents', community?.id, userId ?? '']}
                      />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* ── Right column: sidebar ── */}
            <Grid md={4} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: { md: 'sticky' },
                  top: { md: 24 },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <UserSidebar
                    resident={resident}
                    community={community!}
                    isLoading={isLoading}
                  />
                </Box>
                {resident?.uuid && community && !isViewingSelf && (
                  <>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                      <ArchiveUserCard
                        residentId={resident.uuid}
                        residentName={((`${resident.first_name ?? ''} ${resident.last_name ?? ''}`.trim()) || resident.email) ?? ''}
                      />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
